import type { Component, VoidComponent } from "solid-js";
import { createMemo, createUniqueId, For, Show, untrack } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { useTranslation } from "../../../state/i18n";
import { IconButton, TextAndIconButton } from "../../../components/button/Button";
import FormatDate from "../../../components/formatting/FormatDate";
import EmptyState from "../../../components/emptyState/EmptyState";
import { isEmpty, noop } from "lodash-es";
import { DestructiveModal } from "../../../components/modal/Modal";
import InputModal from "../../../components/modal/InputModal";
import { MAX_API_KEYS } from "../../../constants";
import { CompactTextInput, TextInput } from "../../../components/inputs/TextInput";
import ApiService from "../../../services/ApiService";
import type ApiKeyId from "../../../state/ApiKeyId";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import DetailModal from "../../../components/modal/DetailModal";
import { Header } from "../../../components/inputs/Input";
import Text from "../../../components/text/Text";
import { Icon } from "../../../components/icon/Icon";
import { createApiKeySchema } from "../../../types/ApiKey";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import CopyValueButton from "./CopyValueButton";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";

const ApiKeysPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const { open, replace } = useModalManager();
  const apiKeysQuery = ApiService.getApiKeys();
  const hasReachedApiKeyLimit = createMemo(() =>
    apiKeysQuery.status === "success" ? apiKeysQuery.data.length >= MAX_API_KEYS : false,
  );

  const openCreateApiKeyModal = () => {
    open({
      content: () => {
        const createApiKeyMutation = ApiService.createApiKey();

        return (
          <InputModal
            variant="default"
            size="large"
            closeOnSubmit={false}
            title={t(($) => $.SETTINGS_API_KEYS_CREATE_MODAL_TITLE)}
            titleIcon="lockClosed"
            confirmLabel={t(($) => $.SETTINGS_API_KEYS_CREATE_MODAL_ACTIONS_CONFIRM)}
            confirmLabelSlow={t(($) => $.SETTINGS_API_KEYS_CREATE_MODAL_ACTIONS_CONFIRM_SLOW)}
            cancelLabel={t(($) => $.SETTINGS_API_KEYS_CREATE_MODAL_ACTIONS_CANCEL)}
            onConfirm={(value) =>
              new Promise((resolve) => {
                createApiKeyMutation.mutate(value, {
                  onSuccess: (token) => {
                    const nameInputId = createUniqueId();

                    replace({
                      content: () => (
                        <DetailModal
                          title={t(($) => $.SETTINGS_API_KEYS_CREATED_MODAL_TITLE)}
                          description={t(($) => $.SETTINGS_API_KEYS_CREATED_MODAL_DESCRIPTION)}
                          content={() => (
                            <div class="flex flex-col gap-1">
                              <Header
                                id={nameInputId}
                                label={t(($) => $.SETTINGS_API_KEYS_CREATED_MODAL_INPUT_TOKEN)}
                              />
                              <div class="flex gap-1">
                                <CompactTextInput id={nameInputId} disabled value={token} onBlur={noop} />
                                <CopyValueButton value={token} />
                              </div>
                            </div>
                          )}
                        />
                      ),
                    });
                    resolve();
                  },
                  onError: (err) => {
                    replace({
                      content: () => (
                        <GenericFailureModal
                          title={t(($) => $.SETTINGS_API_KEYS_CREATE_FAILURE_MODAL_TITLE)}
                          error={err}
                        />
                      ),
                    });
                  },
                });
              })
            }
            schema={createApiKeySchema}
          >
            {({ value, updateValue, error }) => (
              <div class="flex flex-col gap-4">
                <TextInput
                  label={t(($) => $.SETTINGS_API_KEYS_CREATE_MODAL_INPUT_NAME)}
                  value={value("name")}
                  onBlur={(e) => {
                    updateValue("name", e.target.value);
                  }}
                  onInput={(e) => {
                    updateValue("name", e.target.value);
                  }}
                  errorMessage={error("name")}
                />
              </div>
            )}
          </InputModal>
        );
      },
    });
  };

  const openDeleteApiKeyModal = (id: ApiKeyId) => {
    open({
      content: () => <DeleteApiKeyModal apiKeyId={id} />,
    });
  };

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_CONNECTIONS_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_API_KEYS_LABEL)}
        actionSlot={() => (
          <Show when={!isEmpty(apiKeysQuery.data) && !hasReachedApiKeyLimit()}>
            <div class="ml-auto">
              <TextAndIconButton
                variant="light"
                size="large"
                text={t(($) => $.SETTINGS_API_KEYS_CREATE_BUTTON_LABEL)}
                icon="plus"
                onClick={() => {
                  openCreateApiKeyModal();
                }}
              />
            </div>
          </Show>
        )}
      />

      <Show when={hasReachedApiKeyLimit()}>
        <div class="pt-6">
          <InlineMessage
            variant="warning"
            label={t(($) => $.SETTINGS_API_KEYS_LIMIT_REACHED, { limit: MAX_API_KEYS })}
          />
        </div>
      </Show>

      <div class="relative h-full min-h-0" aria-busy={apiKeysQuery.isFetching}>
        <Show
          when={apiKeysQuery.data}
          fallback={
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">{t(($) => $.SETTINGS_API_KEYS_LOADING_TITLE)}</span>
            </div>
          }
        >
          {(data) => (
            <div class="h-full pt-8">
              <Show when={!isEmpty(data())} fallback={<NoApiKeys actionOnClick={openCreateApiKeyModal} />}>
                <div
                  role="table"
                  style={{ "--cols": 4 }}
                  class="flex size-full border-collapse flex-col overflow-hidden"
                >
                  <div role="rowgroup">
                    <div
                      role="row"
                      class="grid grid-cols-auto-min-table gap-3 border-b border-subtle text-base-semibold text-secondary"
                    >
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_API_KEYS_TABLE_HEADER_NAME)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_API_KEYS_TABLE_HEADER_TOKEN)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_API_KEYS_TABLE_HEADER_CREATED_ON)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_API_KEYS_TABLE_HEADER_DELETE)}
                      </div>
                    </div>
                  </div>
                  <div role="rowgroup" class="h-full divide-y divide-subtle overflow-y-auto pb-6">
                    <For each={data()}>
                      {(apiKey) => (
                        <div
                          role="row"
                          class="grid grid-cols-auto-min-table items-center gap-3 overflow-hidden text-base-regular text-secondary"
                        >
                          <div role="cell" class="flex">
                            <Text class="py-4" text={apiKey.name} truncate />
                          </div>
                          <div role="cell" class="min-w-30 py-4">
                            •••••{apiKey.lastCharacters}
                          </div>
                          <div role="cell" class="min-w-30 py-4">
                            <FormatDate value={apiKey.createdOn} />
                          </div>
                          <div role="cell" class="py-4">
                            <IconButton
                              variant="tertiary"
                              size="medium"
                              icon="trash"
                              aria-label={t(($) => $.DELETE_ARIA)}
                              onClick={() => {
                                openDeleteApiKeyModal(apiKey.id);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

type NoApiKeysProps = {
  actionOnClick: () => void;
};
const NoApiKeys: VoidComponent<NoApiKeysProps> = (props) => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.SETTINGS_API_KEYS_TABLE_EMPTY_STATE_TITLE)}
      description={t(($) => $.SETTINGS_API_KEYS_TABLE_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "lockClosed" }}
      action={{
        variant: "light",
        icon: "plus",
        text: t(($) => $.SETTINGS_API_KEYS_TABLE_EMPTY_STATE_ACTION_LABEL),
        onClick: () => {
          props.actionOnClick();
        },
      }}
    />
  );
};

type DeleteApiKeyModalProps = {
  apiKeyId: ApiKeyId;
};
const DeleteApiKeyModal: Component<DeleteApiKeyModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();

  return (
    <DestructiveModal
      title={t(($) => $.SETTINGS_API_KEYS_DELETE_MODAL_TITLE)}
      description={t(($) => $.SETTINGS_API_KEYS_DELETE_MODAL_DESCRIPTION)}
      confirmLabel={t(($) => $.SETTINGS_API_KEYS_DELETE_MODAL_ACTIONS_CONFIRM)}
      confirmLabelSlow={t(($) => $.SETTINGS_API_KEYS_DELETE_MODAL_ACTIONS_CONFIRM_SLOW)}
      cancelLabel={t(($) => $.SETTINGS_API_KEYS_DELETE_MODAL_ACTIONS_CANCEL)}
      onConfirm={() =>
        new Promise((resolve, reject) => {
          ApiService.deleteApiKey().mutate(
            untrack(() => props.apiKeyId),
            {
              onSuccess: () => {
                resolve();
              },
              onError: (err) => {
                replace({
                  content: () => (
                    <GenericFailureModal title={t(($) => $.SETTINGS_API_KEYS_DELETE_FAILURE_MODAL_TITLE)} error={err} />
                  ),
                });
                reject(err);
              },
            },
          );
        })
      }
    />
  );
};

const ProtectedApiKeysPage = withPagePermissionCheck(ApiKeysPage, PermissionService.canManageApiKeys);

export default ProtectedApiKeysPage;
